/** ตัวช่วยฝั่งแสดงผลของหน้าจอ SSO admin — ไม่เรียก API */

import type { SsoOauthClient, UpdateSsoClientBody } from '../../lib/ssoAdmin/types';

/** หนึ่งบรรทัดต่อหนึ่งค่า — ตัดช่องว่าง ทิ้งบรรทัดว่างและค่าซ้ำ */
export function splitLines(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const value = line.trim();
    if (value !== '' && !seen.has(value)) {
      seen.add(value);
      out.push(value);
    }
  }
  return out;
}

export interface RoleLine {
  readonly role_code: string;
  readonly description: string;
}

const ROLE_CODE_RE = /^[A-Z][A-Z0-9_]*$/;

/** `ROLE_CODE คำอธิบาย` ต่อบรรทัด — `null` เมื่อมีบรรทัดที่ผิดรูปหรือไม่มีบรรทัดเลย */
export function parseRoleLines(text: string): RoleLine[] | null {
  const lines = splitLines(text);
  if (lines.length === 0) {
    return null;
  }
  const roles: RoleLine[] = [];
  for (const line of lines) {
    const match = /^(\S+)\s+(.+)$/.exec(line);
    if (match === null || !ROLE_CODE_RE.test(match[1])) {
      return null;
    }
    roles.push({ role_code: match[1], description: match[2].trim() });
  }
  return new Set(roles.map((r) => r.role_code)).size === roles.length ? roles : null;
}

/** ตัด `T` และเศษวินาทีทิ้งเพื่ออ่านง่าย โดยคง offset เวลาไว้ */
export function formatTimestamp(value: string | null): string {
  if (value === null) {
    return '—';
  }
  return value.replace('T', ' ').replace(/\.\d+/, '');
}

export interface ClientEditForm {
  readonly client_name: string;
  readonly redirectText: string;
  readonly postLogoutText: string;
  readonly mfaRoles: readonly string[];
  readonly refresh_enabled: boolean;
  readonly allow_insecure_localhost: boolean;
  readonly require_app_role: boolean;
  readonly active: boolean;
}

export function toEditForm(client: SsoOauthClient): ClientEditForm {
  return {
    client_name: client.client_name,
    redirectText: client.redirect_uris.join('\n'),
    postLogoutText: client.post_logout_redirect_uris.join('\n'),
    mfaRoles: client.mfa_roles,
    refresh_enabled: client.refresh_enabled,
    allow_insecure_localhost: client.allow_insecure_localhost,
    require_app_role: client.require_app_role,
    active: client.active,
  };
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/** เฉพาะฟิลด์ที่เปลี่ยนจริง — ฟิลด์ที่ไม่ส่งคือคงเดิม จึงไม่ถูกตรวจ URI ซ้ำโดยไม่จำเป็น */
export function diffEditForm(client: SsoOauthClient, form: ClientEditForm): UpdateSsoClientBody {
  const body: {
    -readonly [K in keyof UpdateSsoClientBody]: UpdateSsoClientBody[K];
  } = {};
  const name = form.client_name.trim();
  if (name !== client.client_name) body.client_name = name;
  const redirects = splitLines(form.redirectText);
  if (!sameList(redirects, client.redirect_uris)) body.redirect_uris = redirects;
  const postLogout = splitLines(form.postLogoutText);
  if (!sameList(postLogout, client.post_logout_redirect_uris)) {
    body.post_logout_redirect_uris = postLogout;
  }
  const mfa = [...form.mfaRoles].sort();
  if (!sameList(mfa, [...client.mfa_roles].sort())) body.mfa_roles = mfa;
  if (form.refresh_enabled !== client.refresh_enabled) body.refresh_enabled = form.refresh_enabled;
  if (form.allow_insecure_localhost !== client.allow_insecure_localhost) {
    body.allow_insecure_localhost = form.allow_insecure_localhost;
  }
  if (form.require_app_role !== client.require_app_role) {
    body.require_app_role = form.require_app_role;
  }
  if (form.active !== client.active) body.active = form.active;
  return body;
}
